// =============================================================================
// Refonte Documents du dossier (OH AMO) — STRUCTURE CIBLE (validée JJ)
// -----------------------------------------------------------------------------
// NAVIGATION = MODE MENU (pas de sections empilées). L'utilisateur navigue par
// menu ; une entrée affichée à la fois.
//
//   EN HAUT  — Génération Devis/Contrat AMO : « Générer » SEUL (pas de PDF /
//              Télécharger / Régénérer ici).
//   MENU     — 1. Documents AMO   (Devis, Contrat)      → Générer · PDF · Télécharger · Signer
//              2. Documents ANAH  (Cerfa : Mandat, Avance, Plan fin., Procuration)
//                                                        → Générer · PDF · Télécharger · Signer
//              3. Pièces du dossier (justificatifs importés — DocumentsForm, comme OMV)
//              4. Données MPA       (DonneesMPR — contient le sélecteur MANDATAIRE,
//                                    source unique ; le doublon du haut est retiré)
//
// Règles : un doc = une ligne · seules les actions disponibles sont montrées ·
// zéro couleur en dur (tokens var(--oh-*)) · sentence case.
//
// Périmètre : TEF uniquement. Le parcours OMV/CEE reste inchangé (voir
// DocumentsAides.jsx early-return isTEF). DocuSign = « Préparer la signature »
// (envoi par doc via /docusign/send-pdf — le workflow GROUPÉ n'existe pas encore
// côté back, suivi séparé).
// =============================================================================
import { useState, useEffect } from "react";
import {
  FileText, Receipt, FileCheck, FileSignature, Landmark, PenTool,
  FolderOpen, Eye, Download, Send, RefreshCw, Loader2, Lock, ArrowRight,
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { API, getAuthHeaders, openAuthedPdf, formatMontant, formatDate } from "./helpers";
import { toast } from "sonner";
import { DocumentsForm } from "./DocumentsForm";

// ─── Modèles de documents TEF (source unique locale) ─────────────────────────
// Chaque entrée décrit : type_document (clé côté /devis-contrats), libellé, slug
// de download, et le mode de génération. « generate: wizard » ouvre l'assistant
// devis ; « generate: <endpoint POST> » appelle le générateur TEF existant.
const DEFS_AMO = [
  { key: "devis_amo", label: "Devis AMO", icon: Receipt, slug: "devis-amo", desc: "Devis d'accompagnement AMO MaPrimeAdapt'", generate: "wizard" },
  { key: "contrat_amo", label: "Contrat AMO", icon: FileCheck, slug: "contrat-amo", desc: "Contrat d'accompagnement AMO", generate: "wizard" },
];
// Documents signables (AMO + ANAH) : type_document → libellé + slug de download.
// Sert au workflow GROUPÉ (récupère chaque PDF via son slug pour l'envoi bundle).
const SIGNABLE_MAP = {
  devis_amo:                { label: "Devis AMO",                   slug: "devis-amo" },
  contrat_amo:              { label: "Contrat AMO",                 slug: "contrat-amo" },
  mandat_amo:               { label: "Mandat ANAH",                slug: "mandat-amo" },
  demande_avance_anah:      { label: "Demande d'avance ANAH",       slug: "demande-avance-anah" },
  plan_financement:         { label: "Plan de financement",        slug: "plan-financement" },
  procuration_seing_prive:  { label: "Procuration (Cerfa 13463)",  slug: "procuration-seing-prive" },
};

// ─── Dialog DocuSign GROUPÉ (Lot 2, Req 3) ───────────────────────────────────
// « Préparer la signature » = envoi de PLUSIEURS documents dans UNE enveloppe.
// Mode « DocuSign non configuré » propre (bannière + envoi désactivé) — testable
// en preprod ; l'envoi réel de l'enveloppe est validé au port prod.
function GroupedSignatureDialog({ signables, dossierId, tenantId, dossier, docusignConfigured, onClose }) {
  const [selected, setSelected] = useState(() => new Set(signables.map(s => s.type_document)));
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    signerName: dossier?.client_nom || "",
    signerEmail: dossier?.client_email || dossier?.client?.email || "",
    subject: `Documents à signer — ${dossier?.référence || ""}`,
  });

  const toggle = (t) => setSelected(prev => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; });

  const send = async () => {
    const chosen = signables.filter(s => selected.has(s.type_document));
    if (!chosen.length) { toast.error("Sélectionnez au moins un document"); return; }
    if (!form.signerEmail || !form.signerName) { toast.error("Renseignez le nom et l'email du signataire"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      for (const s of chosen) {
        const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/${s.slug}/download`, { headers: getAuthHeaders(tenantId), credentials: "include" });
        if (!res.ok) throw new Error(`PDF indisponible : ${s.label}`);
        const blob = await res.blob();
        fd.append("files", blob, `${s.slug}.pdf`);
        fd.append("names", s.label);
      }
      fd.append("signer_email", form.signerEmail);
      fd.append("signer_name", form.signerName);
      fd.append("email_subject", form.subject || "Documents à signer");
      if (dossier?.id) fd.append("dossier_id", dossier.id);
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/docusign/send-bundle`, {
        method: "POST",
        headers: { "X-Tenant-ID": tenantId || "tef", ...(token && { Authorization: `Bearer ${token}` }) },
        body: fd,
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.message || `${chosen.length} document(s) envoyé(s) pour signature`);
        onClose();
      } else if (res.status === 503) {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "DocuSign non configuré pour ce compte");
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Erreur lors de l'envoi groupé");
      }
    } catch (e) { toast.error(e.message || "Erreur lors de l'envoi groupé"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--oh-bg)] backdrop-blur-sm p-4">
      <div className="rounded-xl p-6 w-full max-w-md shadow-2xl" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="grouped-signature-dialog">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg" style={{ background: "var(--oh-accent-bg)" }}><FileSignature className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} /></div>
          <div>
            <h3 className="font-medium" style={{ color: "var(--oh-text)" }}>Préparer la signature</h3>
            <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>Envoi groupé — un email, plusieurs documents.</p>
          </div>
        </div>
        {!docusignConfigured && (
          <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", border: "1px solid var(--oh-stat-amber-border)" }} data-testid="docusign-not-configured">
            DocuSign non configuré pour ce compte — la préparation est disponible ; l'envoi réel sera possible une fois DocuSign activé.
          </div>
        )}
        {signables.length === 0 ? (
          <p className="text-sm mb-4" style={{ color: "var(--oh-text-muted)" }}>Aucun document généré à signer. Générez d'abord les documents.</p>
        ) : (
          <div className="space-y-2 mb-4">
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Documents à envoyer ensemble :</p>
            {signables.map(s => (
              <label key={s.type_document} className="flex items-center gap-2.5 p-2 rounded-lg cursor-pointer" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
                <input type="checkbox" checked={selected.has(s.type_document)} onChange={() => toggle(s.type_document)} className="w-4 h-4 accent-[var(--oh-accent-solid)]" data-testid={`bundle-check-${s.type_document}`} />
                <span className="text-sm" style={{ color: "var(--oh-text)" }}>{s.label}</span>
              </label>
            ))}
          </div>
        )}
        <div className="space-y-3">
          <Input value={form.signerName} onChange={(e) => setForm(f => ({ ...f, signerName: e.target.value }))} placeholder="Nom du signataire" data-testid="bundle-signer-name" />
          <Input type="email" value={form.signerEmail} onChange={(e) => setForm(f => ({ ...f, signerEmail: e.target.value }))} placeholder="Email du signataire" data-testid="bundle-signer-email" />
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={sending}>Annuler</Button>
          <Button className="flex-1" style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }} onClick={send} disabled={sending || !docusignConfigured || signables.length === 0} data-testid="bundle-send">
            {sending ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Envoyer pour signature
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Liste de documents d'une entrée de menu (AMO ou ANAH) ───────────────────
// Un doc = une ligne. Seules les actions disponibles selon l'état sont montrées :
// non généré → « Générer » ; généré → PDF · Télécharger · Préparer la signature.
// mode : "amo" = bibliothèque (Voir/Télécharger/Préparer signature ; génération en
// Zone 1) | "anah" = pièces officielles (Générer/Régénérer + Voir/Télécharger/
// Préparer signature ; génération sur place).
function TefDocList({ defs, docs, dossierId, tenantId, dossier, onNavigate, reload, emptyLabel, mode = "anah" }) {
  const [busyKey, setBusyKey] = useState(null);
  const hasMandataire = !!(dossier?.donnees_mpa?.mandataire_id
    || dossier?.donnees_mpr?.mandataire_id
    || dossier?.workflow_data?.donnees_mpr?.mandataire_id
    || dossier?.workflow_data?.tef?.donnees_mpr?.mandataire_id);

  const generate = async (def) => {
    if (def.generate === "wizard") {
      onNavigate?.("devis-wizard", { dossierId, devisId: "new" });
      return;
    }
    setBusyKey(def.key);
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/${def.generate}`, {
        method: "POST", headers: getAuthHeaders(tenantId), credentials: "include",
      });
      if (res.ok) {
        toast.success(`${def.label} généré`);
        await reload();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e?.detail?.message || e?.detail || `Erreur génération ${def.label}`);
      }
    } catch { toast.error(`Erreur génération ${def.label}`); }
    finally { setBusyKey(null); }
  };

  const view = async (def) => {
    await openAuthedPdf({
      url: `${API}/api/metier/tef/dossiers/${dossierId}/${def.slug}/download`,
      headers: getAuthHeaders(tenantId),
      onError: (m) => toast.error(m || "Erreur à l'ouverture"),
    });
  };

  const download = async (def) => {
    setBusyKey(def.key + ":dl");
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/${def.slug}/download`, { headers: getAuthHeaders(tenantId), credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${def.slug}-${dossierId}.pdf`; a.click();
        URL.revokeObjectURL(url);
      } else { toast.error(`Erreur ${res.status} au téléchargement`); }
    } catch (e) { toast.error(`Erreur réseau : ${e.message}`); }
    finally { setBusyKey(null); }
  };

  return (
    <div className="space-y-2.5">
      {defs.map((def) => {
        const doc = docs.find((d) => d.type_document === def.key);
        const generated = !!(doc && (doc.pdf_genere || doc.pdf_url));
        const signed = !!doc?.is_signed;
        const Icon = def.icon;
        const mandatBlocked = def.requiresMandataire && !hasMandataire && !generated;
        return (
          <div
            key={def.key}
            className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg"
            style={{
              background: signed ? "var(--oh-stat-green-bg)" : generated ? "var(--oh-bg-surface)" : "var(--oh-bg-elevated)",
              border: `1px solid ${signed ? "var(--oh-stat-green-border)" : "var(--oh-border)"}`,
            }}
            data-testid={`docrow-${def.key}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg" style={{ background: "var(--oh-accent-bg)" }}>
                <Icon className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm" style={{ color: "var(--oh-text)" }}>{def.label}</span>
                  {signed
                    ? <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }}>Signé</Badge>
                    : generated
                      ? <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)" }}>Généré</Badge>
                      : <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)" }}>Non généré</Badge>}
                </div>
                <p className="text-xs mt-0.5" style={{ color: "var(--oh-text-muted)" }}>{def.desc}</p>
                {mandatBlocked && (
                  <p className="text-xs mt-0.5" style={{ color: "var(--oh-stat-amber-text)" }}>
                    Définir le mandataire dans « Données MPA » d'abord.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Génération : Zone 3 (ANAH) génère/régénère sur place ; Zone 2 (AMO) NON
                  — Devis/Contrat se (ré)génèrent en Zone 1 « en haut ». */}
              {mode === "anah" && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => generate(def)}
                  disabled={busyKey === def.key || mandatBlocked}
                  data-testid={`${generated ? "regenerate" : "generate"}-${def.key}`}
                >
                  {busyKey === def.key ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : generated ? <RefreshCw className="w-3.5 h-3.5 mr-1" /> : <FileSignature className="w-3.5 h-3.5 mr-1" />}
                  {generated ? "Régénérer" : "Générer"}
                </Button>
              )}
              {!generated && mode === "amo" && (
                <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>À générer en haut</span>
              )}
              {generated && (
                <>
                  <Button variant="outline" size="sm" onClick={() => view(def)} data-testid={`view-${def.key}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Voir
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => download(def)} disabled={busyKey === def.key + ":dl"} data-testid={`download-${def.key}`}>
                    {busyKey === def.key + ":dl" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Télécharger</>}
                  </Button>
                  {/* Signature : workflow GROUPÉ au niveau Documents (bouton « Préparer
                      la signature » global) — plus de signature par doc isolé (Req 3). */}
                </>
              )}
            </div>
          </div>
        );
      })}
      {defs.length === 0 && (
        <div className="text-center py-8" style={{ color: "var(--oh-text-muted)" }}>
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{emptyLabel || "Aucun document"}</p>
        </div>
      )}
    </div>
  );
}

// ─── Entrée « Pièces du dossier » : justificatifs importés (comme OMV) ────────
// Réutilise DocumentsForm (upload/liste/suppression), sans filtre restrictif :
// on affiche TOUS les justificatifs collectés (CNI, avis d'impôt, RIB…).
function PiecesEntry({ dossierId, tenantId, dossier, onUpdate }) {
  const [documents, setDocuments] = useState([]);
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/documents`, {
          headers: { "X-Tenant-ID": tenantId, ...(token && { Authorization: `Bearer ${token}` }) },
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setDocuments(Array.isArray(data) ? data : data.documents || []);
        }
      } catch (e) { console.error(e); }
    };
    if (dossierId) load();
  }, [dossierId, tenantId]);
  return (
    <DocumentsForm
      dossierId={dossierId}
      tenantId={tenantId}
      documents={documents}
      verrouille={dossier?.verrouille}
      onUpdate={onUpdate}
      clientNom={dossier?.client_nom}
      dossierRef={dossier?.référence}
    />
  );
}

// ─── Zone 3 « Documents ANAH » DATA-DRIVEN (Lot 2, Req 4) ────────────────────
// Le front BOUCLE sur la config (endpoint adaptateur /anah/documents) : plus de
// liste hardcodée. Sous-sections Avant/Après = champ `phase`. Actions via les
// chemins résolus par l'adaptateur (generate_path/download_path) — le front ne
// connaît aucune route en dur. « Afficher si en modèle ET générable » = géré côté
// endpoint (docs actifs dont l'identifiant est résolu par l'adaptateur).
const ANAH_PHASES = [
  { key: "avant_travaux", label: "Avant travaux" },
  { key: "apres_travaux", label: "Après travaux" },
];

function AnahZone({ dossierId, tenantId, dossier }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const hasMandataire = !!(dossier?.donnees_mpa?.mandataire_id
    || dossier?.donnees_mpr?.mandataire_id
    || dossier?.workflow_data?.donnees_mpr?.mandataire_id
    || dossier?.workflow_data?.tef?.donnees_mpr?.mandataire_id);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/anah/documents`, { headers: getAuthHeaders(tenantId), credentials: "include" });
      if (res.ok) { const d = await res.json(); setDocs(d.documents || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [dossierId]);

  const generate = async (cfg) => {
    setBusyKey(cfg.code);
    try {
      const res = await fetch(`${API}${cfg.generate_path}`, { method: "POST", headers: getAuthHeaders(tenantId), credentials: "include" });
      if (res.ok) { toast.success(`${cfg.libelle} ${cfg.genere ? "régénéré" : "généré"}`); await load(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e?.detail?.message || e?.detail || `Erreur génération ${cfg.libelle}`); }
    } catch { toast.error(`Erreur génération ${cfg.libelle}`); }
    finally { setBusyKey(null); }
  };
  const view = async (cfg) => {
    await openAuthedPdf({ url: `${API}${cfg.download_path}`, headers: getAuthHeaders(tenantId), onError: (m) => toast.error(m || "Erreur à l'ouverture") });
  };
  const download = async (cfg) => {
    setBusyKey(cfg.code + ":dl");
    try {
      const res = await fetch(`${API}${cfg.download_path}`, { headers: getAuthHeaders(tenantId), credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${cfg.code}-${dossierId}.pdf`; a.click();
        URL.revokeObjectURL(url);
      } else { toast.error(`Erreur ${res.status} au téléchargement`); }
    } catch (e) { toast.error(`Erreur réseau : ${e.message}`); }
    finally { setBusyKey(null); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>;
  if (docs.length === 0) return (
    <div className="text-center py-8" style={{ color: "var(--oh-text-muted)" }}>
      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">Aucune pièce ANAH configurée</p>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="anah-zone-datadriven">
      {ANAH_PHASES.map((ph) => {
        const items = docs.filter((d) => d.phase === ph.key);
        if (!items.length) return null;
        return (
          <div key={ph.key} data-testid={`anah-phase-${ph.key}`}>
            <h4 className="text-xs font-medium uppercase tracking-wide mb-2" style={{ color: "var(--oh-text-muted)" }}>{ph.label}</h4>
            <div className="space-y-2.5">
              {items.map((cfg) => {
                const mandatBlocked = cfg.code === "mandat_anah" && !hasMandataire && !cfg.genere;
                return (
                  <div key={cfg.code} className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg"
                    style={{ background: cfg.is_signed ? "var(--oh-stat-green-bg)" : cfg.genere ? "var(--oh-bg-surface)" : "var(--oh-bg-elevated)", border: `1px solid ${cfg.is_signed ? "var(--oh-stat-green-border)" : "var(--oh-border)"}` }}
                    data-testid={`anahrow-${cfg.code}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 rounded-lg" style={{ background: "var(--oh-accent-bg)" }}><FileSignature className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} /></div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm" style={{ color: "var(--oh-text)" }}>{cfg.libelle}</span>
                          {cfg.is_signed
                            ? <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }}>Signé</Badge>
                            : cfg.genere
                              ? <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)" }}>Généré</Badge>
                              : <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)" }}>Non généré</Badge>}
                        </div>
                        {mandatBlocked && <p className="text-xs mt-0.5" style={{ color: "var(--oh-stat-amber-text)" }}>Définir le mandataire dans « Données MPA » d'abord.</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => generate(cfg)} disabled={busyKey === cfg.code || mandatBlocked} data-testid={`${cfg.genere ? "regenerate" : "generate"}-${cfg.code}`}>
                        {busyKey === cfg.code ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" />{cfg.genere ? "Régénérer" : "Générer"}</>}
                      </Button>
                      {cfg.genere && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => view(cfg)} data-testid={`view-${cfg.code}`}><Eye className="w-3.5 h-3.5 mr-1" /> Voir</Button>
                          <Button variant="outline" size="sm" onClick={() => download(cfg)} disabled={busyKey === cfg.code + ":dl"} data-testid={`download-${cfg.code}`}>
                            {busyKey === cfg.code + ":dl" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Télécharger</>}
                          </Button>
                          {/* Signature : workflow GROUPÉ global (Req 3), plus par doc isolé. */}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── EN HAUT — Génération Devis/Contrat AMO ──────────────────────────────────
// Affiche le DÉTAIL des documents commerciaux (numéro, montant TTC, date, badge).
// Bouton : « Générer » (assistant devis) si non généré → « Régénérer » (POST du
// générateur TEF) si déjà généré. PAS de Voir/Télécharger ici : ces actions
// restent dans l'entrée de menu « Documents AMO » (un doc = une source d'actions).
function TopGeneration({ docs, dossierId, tenantId, onNavigate, reload }) {
  const [busyKey, setBusyKey] = useState(null);

  const regenerate = async (def) => {
    setBusyKey(def.key);
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/${def.slug}/generer`, {
        method: "POST", headers: getAuthHeaders(tenantId), credentials: "include",
      });
      if (res.ok) { toast.success(`${def.label} régénéré`); await reload(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e?.detail?.message || e?.detail || `Erreur régénération ${def.label}`); }
    } catch { toast.error(`Erreur régénération ${def.label}`); }
    finally { setBusyKey(null); }
  };

  return (
    <div className="rounded-lg p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="doc-generation-top">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg" style={{ background: "var(--oh-accent-bg)" }}>
          <PenTool className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Devis et contrat AMO</h3>
          <p className="text-xs mt-0.5" style={{ color: "var(--oh-text-muted)" }}>Génération des documents commerciaux du dossier.</p>
        </div>
      </div>
      <div className="space-y-2">
        {DEFS_AMO.map((def) => {
          const doc = docs.find((d) => d.type_document === def.key);
          const generated = !!(doc && (doc.pdf_genere || doc.pdf_url));
          const montant = doc?.total_ttc ?? doc?.montant_ttc;
          const Icon = def.icon;
          return (
            <div
              key={def.key}
              className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg"
              style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}
              data-testid={`gentop-${def.key}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-lg" style={{ background: "var(--oh-accent-bg)" }}>
                  <Icon className="w-4 h-4" style={{ color: "var(--oh-accent-text)" }} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm" style={{ color: "var(--oh-text)" }}>{def.label}</span>
                    {generated
                      ? <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }}>Généré</Badge>
                      : <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text-muted)" }}>Non généré</Badge>}
                  </div>
                  {generated ? (
                    <p className="text-xs mt-0.5" style={{ color: "var(--oh-text-muted)" }}>
                      {doc.numero ? `${doc.numero} · ` : ""}{montant != null ? `${formatMontant(montant)} TTC · ` : ""}{formatDate(doc.created_at)}
                    </p>
                  ) : (
                    <p className="text-xs mt-0.5" style={{ color: "var(--oh-text-muted)" }}>{def.desc}</p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant={generated ? "outline" : undefined}
                style={generated ? undefined : { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}
                onClick={() => generated ? regenerate(def) : onNavigate?.("devis-wizard", { dossierId, devisId: "new" })}
                disabled={busyKey === def.key}
                data-testid={`gentop-btn-${def.key}`}
              >
                {busyKey === def.key ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : generated ? <RefreshCw className="w-3.5 h-3.5 mr-1" /> : <ArrowRight className="w-3.5 h-3.5 mr-1" />}
                {generated ? "Régénérer" : "Générer"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Composant principal — MENU (Tabs) + génération en tête ──────────────────
export function DocumentsZonesTEF({ dossierId, tenantId, dossier, onNavigate, onUpdate }) {
  const effectiveTenantId = dossier?.tenant_id || tenantId || "tef";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docusignConfigured, setDocusignConfigured] = useState(false);
  const [showBundle, setShowBundle] = useState(false);

  const reload = async () => {
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/devis-contrats`, { headers: getAuthHeaders(effectiveTenantId), credentials: "include" });
      if (res.ok) { const data = await res.json(); setDocs(data.documents || []); }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    (async () => { setLoading(true); await reload(); setLoading(false); })();
    (async () => {
      try {
        const res = await fetch(`${API}/api/docusign/config`, { headers: getAuthHeaders(effectiveTenantId), credentials: "include" });
        if (res.ok) { const c = await res.json(); setDocusignConfigured(c.status === "configured" || c.details?.configured === true); }
      } catch { /* silencieux */ }
    })();
  }, [dossierId]);

  const menuTrigger = "data-[state=active]:bg-[var(--oh-accent-bg)] data-[state=active]:text-[var(--oh-accent-text)] rounded-lg px-4 py-2 text-sm font-medium";

  // Documents signables (AMO + ANAH générés) pour le workflow de signature GROUPÉ.
  const signables = docs
    .filter(d => (d.pdf_genere || d.pdf_url) && SIGNABLE_MAP[d.type_document])
    .map(d => ({ type_document: d.type_document, ...SIGNABLE_MAP[d.type_document] }));

  return (
    <div className="space-y-4" data-testid="documents-zones-tef">
      {/* ── EN HAUT — Génération Devis/Contrat AMO (détail + Générer/Régénérer) ── */}
      <TopGeneration docs={docs} dossierId={dossierId} tenantId={effectiveTenantId} onNavigate={onNavigate} reload={reload} />

      {/* ── Signature GROUPÉE (Req 3) : une action « Préparer la signature » pour
          envoyer plusieurs documents ensemble (pas un bouton par doc isolé). ── */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={async () => { await reload(); setShowBundle(true); }} style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)" }} data-testid="prepare-signature-btn">
          <FileSignature className="w-4 h-4 mr-1.5" /> Préparer la signature
        </Button>
      </div>
      {showBundle && (
        <GroupedSignatureDialog signables={signables} dossierId={dossierId} tenantId={effectiveTenantId} dossier={dossier} docusignConfigured={docusignConfigured} onClose={() => setShowBundle(false)} />
      )}

      {/* ── MENU — une entrée à la fois ── */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>
      ) : (
        <Tabs defaultValue="amo" className="w-full">
          <TabsList className="flex flex-wrap gap-1.5 bg-transparent p-0 h-auto justify-start" data-testid="documents-menu">
            <TabsTrigger value="amo" className={menuTrigger} data-testid="menu-amo"><Receipt className="w-4 h-4 mr-2" />Documents AMO</TabsTrigger>
            <TabsTrigger value="anah" className={menuTrigger} data-testid="menu-anah"><Landmark className="w-4 h-4 mr-2" />Documents ANAH</TabsTrigger>
            <TabsTrigger value="pieces" className={menuTrigger} data-testid="menu-pieces"><FolderOpen className="w-4 h-4 mr-2" />Pièces du dossier</TabsTrigger>
            {/* « Données MPA » retiré ici (décision CTO) : le détail Dossier ANAH +
                Mandataire ANAH détaillé reste UNIQUEMENT dans l'onglet Conformité
                (emplacement historique). Le sélecteur mandataire du HAUT (raccourci
                génération, dans CommercialSection) est conservé. */}
          </TabsList>

          <TabsContent value="amo" className="mt-4">
            <TefDocList defs={DEFS_AMO} mode="amo" docs={docs} dossierId={dossierId} tenantId={effectiveTenantId} dossier={dossier} onNavigate={onNavigate} reload={reload} docusignConfigured={docusignConfigured} emptyLabel="Aucun document AMO" />
          </TabsContent>
          <TabsContent value="anah" className="mt-4">
            <AnahZone dossierId={dossierId} tenantId={effectiveTenantId} dossier={dossier} docusignConfigured={docusignConfigured} />
          </TabsContent>
          <TabsContent value="pieces" className="mt-4">
            <PiecesEntry dossierId={dossierId} tenantId={effectiveTenantId} dossier={dossier} onUpdate={onUpdate} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

export default DocumentsZonesTEF;
